import Drawer from 'components/utility/drawer'
import React from 'react'
import { ACKNOWLEDGEMENT_RESPONSE_STATE } from './constants'

const REVIEW_FLOWS = {
  incomplete: {
    title: 'review response',
    effects: [
      'Notify parties below',
      'Require applicant(s) to resubmit',
      'Suspend acknowledgment period until resubmission',
    ],
    warning: 'Sending this summary will suspend the acknowledgment period + require applicants to resubmit.',
  },
  complete: {
    title: 'review response',
    effects: ['End the acknowledgement period', 'Notify parties below', 'Begin 45-day board review'],
    warning: 'Sending this response will begin the 45-day decision period.',
  },
}

const Sidebar = ({
  flow = ACKNOWLEDGEMENT_RESPONSE_STATE.INCOMPLETE,
  registeredMail,
  recipients,
  pdfReady,
  pollError,
  drawer = {},
  onClose,
  onSend,
  onDownload,
}) => {
  const config = REVIEW_FLOWS[flow]
  const { showDownloadButton = false, sendLabel = 'send' } = drawer
  return (
    <div style={{ width: '377px', flexShrink: 0, padding: '15px 36px 15px 24px', overflow: 'auto', height: '100%' }}>
      <div style={{ marginBottom: 105 }} className="dome-d-flex dome-justify-end">
        <Drawer.Close>
          <button className="dome-btn dome-btn-link" aria-label="Close">
            <div className="dome-d-flex dome-gap6 dome-align-center">
              <i className="fa-re fas fa-times dome-color-med-grey" />
              <span className="dome-p2 dome-color-med-grey">close</span>
            </div>
          </button>
        </Drawer.Close>
      </div>
      <div className="dome-d-flex dome-flex-column">
        <div className="dome-d-flex dome-flex-column dome-gap12">
          <h2 className="dome-h2 dome-color-darkest-grey">{config.title}</h2>
          <div className="dome-d-flex dome-flex-column dome-gap3">
            <span className="dome-p2 dome-color-dark-grey">Sending this response will:</span>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {config.effects.map((e) => (
                <li key={e} className="dome-p2 dome-color-darkest-grey">
                  {e}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div style={{ marginTop: 24, marginBottom: 18, height: 1, background: '#DBDBDB' }}></div>
        <div className="dome-d-flex dome-flex-column dome-gap12">
          <div className="dome-d-flex dome-flex-column dome-gap6">
            <span className="dome-p2 dome-color-dark-grey">registered mail:</span>
            <div style={{ padding: 12, borderRadius: 6, background: '#F9F9F9', border: '1px solid #F0F0F0' }}>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                <li className="dome-p2 dome-color-darkest-grey">{registeredMail?.name}</li>
                <li className="dome-p2 dome-color-darkest-grey">
                  {registeredMail?.address_line1}
                  {registeredMail?.address_line2 ? `, ${registeredMail.address_line2}` : ''}
                </li>
                <li className="dome-p2 dome-color-darkest-grey">
                  {registeredMail?.city}, {registeredMail?.state} {registeredMail?.postal_code}
                </li>
              </ul>
            </div>
          </div>
          <div className="dome-d-flex dome-flex-column dome-gap6">
            <span className="dome-p2 dome-color-dark-grey">email recipient(s):</span>
            <ul
              style={{
                margin: 0,
                padding: 12,
                listStyle: 'none',
                borderRadius: 6,
                background: '#F9F9F9',
                border: '1px solid #F0F0F0',
              }}
            >
              {recipients?.map((recipient) => (
                <li
                  key={recipient.email}
                  className="dome-p2 dome-color-darkest-grey"
                  style={{ wordBreak: 'break-all' }}
                >
                  {recipient.email}
                </li>
              ))}
            </ul>
            <span className="dome-p2 dome-color-dark-grey">All processors on this property will be bcc'd</span>
          </div>
        </div>
        <div style={{ marginTop: 30 }} className="dome-d-flex dome-gap3">
          <span>
            <i className="fa-sharp fa-light fa-alarm-clock dome-color-aqua"></i>
          </span>
          <span className="dome-p2 dome-color-darkest-grey dome-text-w500">{config.warning}</span>
        </div>
        {pollError && (
          <div style={{ marginTop: 12 }}>
            <span className="dome-p2 dome-color-red">{pollError}</span>
          </div>
        )}
        <div style={{ marginTop: 24, marginBottom: 12, height: 1, background: '#DBDBDB' }}></div>
        <div className="dome-d-flex dome-full-width dome-justify-between dome-align-center">
          <button
            onClick={onClose}
            type="button"
            className="dome-btn dome-btn-link dome-btn-link-med-grey dome-btn-link-cancel"
          >
            close
          </button>
          {showDownloadButton ? (
            <div className="dome-d-flex dome-gap6">
              <button
                onClick={onDownload}
                type="button"
                className={`dome-btn dome-btn-base dome-btn-aqua-stroke${!pdfReady ? ' dome-disabled' : ''}`}
                disabled={!pdfReady}
              >
                download PDF
              </button>
              <button
                onClick={onSend}
                type="button"
                className={`dome-btn dome-btn-base dome-btn-dark-green${!pdfReady ? ' dome-disabled' : ''}`}
                disabled={!pdfReady}
              >
                {sendLabel}
              </button>
            </div>
          ) : (
            <button
              onClick={onSend}
              type="button"
              className={`dome-btn dome-btn-base dome-btn-dark-green${!pdfReady ? ' dome-disabled' : ''}`}
              disabled={!pdfReady}
            >
              {sendLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const Body = ({ pdfUrl }) => (
  <div style={{ background: 'var(--accent, #F0F0F0)', padding: '30px 100px 30px 100px', flexGrow: 1 }}>
    <div style={{ overflow: 'auto' }} className="dome-d-flex dome-justify-center dome-full-width dome-full-height">
      {pdfUrl ? (
        <object data={pdfUrl} type="application/pdf" width="100%" height="100%" />
      ) : (
        <div className="dome-d-flex dome-align-center dome-justify-center dome-full-height">
          <span className="dome-p2 dome-color-med-grey">Generating PDF…</span>
        </div>
      )}
    </div>
  </div>
)

export default function AcknowledgementResponseDrawer({
  show,
  flow,
  content = {},
  acknowledgementResponse,
  pdfReady,
  pollError,
  onClose,
  onSubmit,
  onDownload,
  duration,
}) {
  return (
    <Drawer show={show} onClose={onClose} duration={duration}>
      <Drawer.Content wrapperClassName="dome-full-width">
        <div className="dome-d-flex dome-full-height">
          <Body pdfUrl={acknowledgementResponse?.pdf_url} />
          <Sidebar
            flow={flow}
            drawer={content.drawer}
            registeredMail={acknowledgementResponse?.registered_mail}
            recipients={acknowledgementResponse?.recipients}
            pdfReady={pdfReady}
            pollError={pollError}
            onClose={onClose}
            onSend={onSubmit}
            onDownload={onDownload}
          />
        </div>
      </Drawer.Content>
    </Drawer>
  )
}